import React from 'react'
import Highlighter from 'react-highlight-words'
import Select, {
  components,
  StylesConfig,
  SingleValue,
  MultiValue,
  OptionProps,
  GroupBase,
  SelectComponentsConfig
} from 'react-select'

export interface AutocompleteOption {
  label: string
  value: string | number
}

interface AutocompleteProps<T extends AutocompleteOption = AutocompleteOption> {
  options: T[]
  value?: T | T[] | null
  onChange: (value: T | T[] | null) => void
  placeholder?: string
  isMulti?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  className?: string
  error?: string
  label?: string
  required?: boolean
  // 自定义组件
  customComponents?: Partial<
    SelectComponentsConfig<T, boolean, GroupBase<T>>
  >
  // 自定义过滤函数
  customFilterOption?: (option: T, inputValue: string) => boolean
  // 自定义样式
  customStyles?: StylesConfig<T, boolean>
}

// 默认的 Option 组件工厂函数
const createDefaultOption = <T extends AutocompleteOption>() => {
  const DefaultOption: React.ComponentType<
    OptionProps<T, boolean, GroupBase<T>>
  > = (props) => {
    const { data, selectProps } = props
    const inputValue = selectProps.inputValue || ''

    return (
      <components.Option {...props}>
        <div className="flex items-center">
          <Highlighter
            highlightClassName="font-semibold text-blue-600 bg-transparent"
            searchWords={[inputValue]}
            autoEscape={true}
            textToHighlight={data.label}
          />
        </div>
      </components.Option>
    )
  }
  return DefaultOption
}

const customStyles: StylesConfig<AutocompleteOption, boolean> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused
      ? '#3b82f6'
      : state.selectProps.className?.includes('error')
      ? '#ef4444'
      : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#dbeafe'
      : state.isFocused
      ? '#f0f9ff'
      : 'white',
    color: state.isSelected ? '#1e40af' : '#111827',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:active': {
      backgroundColor: '#bfdbfe'
    }
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#111827'
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#eff6ff'
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#1e40af'
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#1e40af',
    '&:hover': {
      backgroundColor: '#3b82f6',
      color: 'white'
    }
  })
}

function Autocomplete<T extends AutocompleteOption = AutocompleteOption>({
  options,
  value,
  onChange,
  placeholder = '请选择...',
  isMulti = false,
  isDisabled = false,
  isLoading = false,
  isClearable = true,
  isSearchable = true,
  className = '',
  error,
  label,
  required = false,
  customComponents,
  customFilterOption,
  customStyles: customStylesProp
}: AutocompleteProps<T>) {
  const handleChange = (
    newValue: SingleValue<T> | MultiValue<T>
  ) => {
    if (isMulti) {
      onChange(newValue as T[] | null)
    } else {
      onChange(newValue as T | null)
    }
  }

  // 使用自定义组件或默认组件
  const finalComponents: Partial<
    SelectComponentsConfig<T, boolean, GroupBase<T>>
  > = customComponents || {
    Option: createDefaultOption<T>()
  }

  // 使用自定义样式或默认样式
  const finalStyles = (customStylesProp || customStyles) as StylesConfig<
    T,
    boolean,
    GroupBase<T>
  >

  // 使用自定义过滤函数或默认过滤函数
  const filterOption = customFilterOption
    ? (option: { data: T }, inputValue: string) => {
        return customFilterOption(option.data, inputValue)
      }
    : (option: { data: T; label: string }, inputValue: string) => {
        if (!inputValue) return true
        const label = option.label.toLowerCase()
        const input = inputValue.toLowerCase()
        return label.includes(input)
      }

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <Select<T, boolean, GroupBase<T>>
        options={options}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isSearchable={isSearchable}
        styles={finalStyles}
        components={finalComponents}
        className={error ? 'error' : ''}
        noOptionsMessage={() => '暂无选项'}
        loadingMessage={() => '加载中...'}
        filterOption={filterOption}
      />

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}

export default Autocomplete
